import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import ConfirmModal from "@/shared/components/ui/Modal/ConfirmModal";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { showErrorFromMutation, showSuccess } from "@/shared/utils/toast";
import { getTextInCapitalize } from "@/shared/utils/helperFunctions";
import {
  creditVoiceWallet,
  logVoiceComplaint,
  resumeVoiceOrganization,
  suspendVoiceOrganization,
  updateVoiceCaps,
} from "./api/superadminApi";

export const VOICE_ORGS_QUERY_KEY = ["super-admin-voice-organizations"];

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 4 });

const CAP_FIELDS = [
  { key: "daily_call_cap", label: "Calls per 24 hours" },
  { key: "monthly_call_cap", label: "Calls per 30 days" },
  { key: "repeat_call_cap", label: "Calls to same number" },
  { key: "repeat_call_window_days", label: "...within days" },
];

function Section({ title, children }) {
  return (
    <section className="flex flex-col gap-3 border-t border-gray-100 pt-4 dark:border-gray-800">
      <h6 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h6>
      {children}
    </section>
  );
}

function useVoiceAdminMutation(mutationFn, successMessage, onDone) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VOICE_ORGS_QUERY_KEY });
      showSuccess(successMessage);
      onDone?.();
    },
    onError: (error) => showErrorFromMutation(error, "Request failed."),
  });
}

export default function VoiceOrgModal({ org, voice, onClose }) {
  const orgId = org?.id;
  const [amount, setAmount] = useState("");
  const [creditNote, setCreditNote] = useState("");
  const [suspendReason, setSuspendReason] = useState("");
  const [caps, setCaps] = useState({});
  const [complaintPhone, setComplaintPhone] = useState("");
  const [complaintNote, setComplaintNote] = useState("");

  useEffect(() => {
    setCaps(Object.fromEntries(CAP_FIELDS.map(({ key }) => [key, voice?.[key] ?? ""])));
  }, [voice]);

  const credit = useVoiceAdminMutation(
    () => creditVoiceWallet(orgId, { amountUsd: Number(amount), note: creditNote.trim() || undefined }),
    "Credit added.",
    () => {
      setAmount("");
      setCreditNote("");
    },
  );
  const suspend = useVoiceAdminMutation(() => suspendVoiceOrganization(orgId, suspendReason.trim()), "Calling suspended.");
  const resume = useVoiceAdminMutation(() => resumeVoiceOrganization(orgId), "Calling resumed.");
  const saveCaps = useVoiceAdminMutation(() => updateVoiceCaps(orgId, caps), "Limits updated.");
  const complaint = useVoiceAdminMutation(
    () => logVoiceComplaint(orgId, { phone: complaintPhone.trim(), note: complaintNote.trim() || undefined }),
    "Complaint logged and calling suspended.",
    () => {
      setComplaintPhone("");
      setComplaintNote("");
    },
  );

  const [confirming, setConfirming] = useState(null);
  const closeConfirm = () => setConfirming(null);
  const orgName = getTextInCapitalize(org?.name || "this organization");
  const suspended = Boolean(voice?.suspended_at);

  return (
    <CustomModal isOpen={Boolean(org)} width="560px" className="p-6">
      <div className="flex w-full flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h5 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Calling: {getTextInCapitalize(org?.name || "")}
            </h5>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {voice?.provisioning_status === "done" ? "Plivo subaccount ready" : `Setup: ${voice?.provisioning_status || "not started"}`}
              {" · "}Balance {usd.format(Number(voice?.wallet_balance_usd || 0))}
              {voice?.default_number ? ` · ${voice.default_number}` : ""}
            </p>
            {suspended && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">Suspended: {voice.suspended_reason}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <X size={18} />
          </button>
        </div>

        <Section title="Add credit">
          <div className="flex flex-col gap-2 sm:flex-row">
            <CommonInput
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="Amount in USD"
            />
            <CommonInput value={creditNote} onChange={(event) => setCreditNote(event.target.value)} placeholder="Note (optional)" />
            <PrimaryButton
              startContent={false}
              onClick={() => credit.mutate()}
              isLoading={credit.isPending}
              disabled={!(Number(amount) > 0) || credit.isPending}
            >
              Add
            </PrimaryButton>
          </div>
        </Section>

        <Section title={suspended ? "Resume calling" : "Suspend calling"}>
          {suspended ? (
            <div className="flex justify-end">
              <PrimaryButton startContent={false} onClick={() => resume.mutate()} isLoading={resume.isPending} disabled={resume.isPending}>
                Resume calling
              </PrimaryButton>
            </div>
          ) : (
            <div className="flex flex-col gap-2 sm:flex-row">
              <CommonInput value={suspendReason} onChange={(event) => setSuspendReason(event.target.value)} placeholder="Reason" />
              <SecondaryButton
                title="Suspend"
                className="border-red-300 text-red-700 dark:border-red-500/40 dark:text-red-300"
                onClick={() => setConfirming("suspend")}
                isLoading={suspend.isPending}
                disabled={suspend.isPending}
              />
            </div>
          )}
        </Section>

        <Section title="Limits">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {CAP_FIELDS.map((field) => (
              <label key={field.key} className="flex flex-col gap-1">
                <span className="text-[11px] text-gray-500 dark:text-gray-400">{field.label}</span>
                <CommonInput
                  type="number"
                  min="0"
                  value={caps[field.key] ?? ""}
                  onChange={(event) => setCaps((current) => ({ ...current, [field.key]: event.target.value }))}
                />
              </label>
            ))}
          </div>
          <div className="flex justify-end">
            <SecondaryButton title="Save limits" onClick={() => saveCaps.mutate()} isLoading={saveCaps.isPending} disabled={saveCaps.isPending} />
          </div>
        </Section>

        <Section title="Log complaint">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Adds the number to the platform do-not-call list and suspends this organization.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <CommonInput value={complaintPhone} onChange={(event) => setComplaintPhone(event.target.value)} placeholder="+14155551234" />
            <CommonInput value={complaintNote} onChange={(event) => setComplaintNote(event.target.value)} placeholder="Note (optional)" />
            <SecondaryButton
              title="Log"
              onClick={() => setConfirming("complaint")}
              isLoading={complaint.isPending}
              disabled={!complaintPhone.trim() || complaint.isPending}
            />
          </div>
        </Section>
      </div>

      <ConfirmModal
        isOpen={confirming === "suspend"}
        destructive
        onClose={closeConfirm}
        title="Suspend calling?"
        description={`Nobody in ${orgName} can make calls until you resume it.`}
        confirmText="Suspend"
        isLoading={suspend.isPending}
        onConfirm={() => suspend.mutate(undefined, { onSettled: closeConfirm })}
      />
      <ConfirmModal
        isOpen={confirming === "complaint"}
        destructive
        onClose={closeConfirm}
        title="Log this complaint?"
        description={`${complaintPhone.trim()} is added to the do-not-call list and calling is suspended for ${orgName}.`}
        confirmText="Log and suspend"
        isLoading={complaint.isPending}
        onConfirm={() => complaint.mutate(undefined, { onSettled: closeConfirm })}
      />
    </CustomModal>
  );
}
